import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { PasswordInput } from "../components/PasswordInput";
import { AuthCard } from "./VerifyEmailPage";

/** The outcome of a confirm attempt. Pure so it can be unit-tested. */
export type ResetResult = { ok: true } | { ok: false; message: string };

/**
 * Client-side rules for the new password.
 *
 * The same shape the server enforces (`PasswordResetConfirmSerializer`): at
 * least 10 characters mixing letters and numbers. Checking here turns the most
 * common mistake into instant feedback without a round trip; the server still
 * rejects anything that slips through.
 */
export function validateNewPassword(password: string, confirm: string): string | null {
  if (!password) return "Choose a password.";
  if (password !== confirm) return "The passwords do not match.";
  if (password.length < 10) return "Use at least 10 characters.";
  if (/^\d+$/.test(password) || /^[A-Za-z]+$/.test(password)) {
    return "Use a mix of letters and numbers.";
  }
  return null;
}

/** The user-facing message for a failed confirm call. Never includes the token. */
export function resetErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return (
      error.fields.token?.join(" ") ||
      error.fields.password?.join(" ") ||
      error.message
    );
  }
  return "Could not reach the server. Please try again.";
}

/**
 * Confirm a reset. Separated from the component so the success, validation and
 * failure paths are testable without a DOM (the suite renders server-side).
 */
export async function submitPasswordReset(
  token: string,
  password: string,
  confirm: string,
  confirmRequest: (token: string, password: string) => Promise<unknown> = (t, p) =>
    api.passwordResetConfirm(t, p),
): Promise<ResetResult> {
  const invalid = validateNewPassword(password, confirm);
  if (invalid) return { ok: false, message: invalid };
  try {
    await confirmRequest(token, password);
    return { ok: true };
  } catch (caught) {
    return { ok: false, message: resetErrorMessage(caught) };
  }
}

/** The terminal success view, after the password has been changed. */
export function ResetPasswordSuccess() {
  return (
    <>
      <p
        role="status"
        className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
      >
        Your password has been reset. You can now sign in with your new password.
      </p>
      <Link
        to="/signin"
        className="mt-4 inline-block w-full rounded-md bg-slate-900 px-3 py-2.5 text-center text-sm font-medium text-white hover:bg-slate-800"
      >
        Back to sign in
      </Link>
    </>
  );
}

/**
 * Password-reset landing page.
 *
 * Reads the single-use token from the URL query -- never a form field, never
 * state -- and posts it to the confirm endpoint. Invalid, expired and
 * already-used tokens are reported from the server's field message. The token
 * and the password are never logged or rendered.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <AuthCard title="Reset your password">
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          This reset link is invalid or incomplete. Request a new one.
        </p>
        <p className="mt-6 text-sm text-slate-500">
          <Link to="/signin" className="font-medium text-slate-900 hover:underline">
            Back to sign in
          </Link>
        </p>
      </AuthCard>
    );
  }

  async function onSubmit(formEvent: FormEvent) {
    formEvent.preventDefault();
    setSubmitting(true);
    setMessage(null);
    const result = await submitPasswordReset(token, password, confirm);
    setSubmitting(false);
    if (result.ok) setDone(true);
    else setMessage(result.message);
  }

  return (
    <AuthCard title="Reset your password">
      {done ? (
        <ResetPasswordSuccess />
      ) : (
        <>
          <p className="mb-4 text-sm text-slate-500">
            Choose a new password for your account.
          </p>
          <form onSubmit={onSubmit} className="space-y-3">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              New password
            </label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={setPassword}
            />
            <p className="text-xs text-slate-400">
              At least 10 characters, with letters and numbers.
            </p>
            <label htmlFor="confirm" className="block text-sm font-medium text-slate-700">
              Confirm password
            </label>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={setConfirm}
            />
            {message ? (
              <p role="alert" className="text-sm text-rose-600">
                {message}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-slate-900 px-3 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Resetting…" : "Reset password"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            <Link to="/signin" className="font-medium text-slate-900 hover:underline">
              Back to sign in
            </Link>
          </p>
        </>
      )}
    </AuthCard>
  );
}
